import { useState, useEffect, useRef, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { format } from "date-fns";
import {
  Note,
  Image as ImageIcon,
  Eye,
  EyeSlash,
  ArrowsClockwise,
  CalendarBlank,
} from "phosphor-react";
import { PhotoThumbnail, PhotoLightbox } from "./photo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Virtual scroll container for performance
export default function JobTimeline({ jobId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [visitsCount, setVisitsCount] = useState(0);
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  const ITEMS_PER_PAGE = 20;

  const fetchTimeline = useCallback(async (skip = 0, append = false) => {
    if (!append) setLoading(true);
    else setLoadingMore(true);

    try {
      const response = await fetch(
        `${API}/jobs/${jobId}/timeline?skip=${skip}&limit=${ITEMS_PER_PAGE}`,
        { credentials: "include" }
      );

      if (response.ok) {
        const data = await response.json();
        if (append) {
          setItems((prev) => [...prev, ...data.items]);
        } else {
          setItems(data.items);
        }
        setTotal(data.total);
        setHasMore(data.has_more);
        setVisitsCount(data.visits_count);
      }
    } catch (error) {
      console.error("Error fetching timeline:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  // Intersection observer for lazy loading more items
  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect();

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore) {
          fetchTimeline(items.length, true);
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => observerRef.current?.disconnect();
  }, [hasMore, loadingMore, items.length, fetchTimeline]);

  const refresh = () => fetchTimeline();

  // Group items by visit for display
  const groupedByVisit = items.reduce((acc, item) => {
    const visitId = item.visit_id || "no-visit";
    if (!acc[visitId]) {
      acc[visitId] = {
        visit: item.visit,
        items: [],
      };
    }
    acc[visitId].items.push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-24 bg-zinc-100 rounded animate-pulse" />
        ))}
      </div>
    );
  }

  if (!items.length) {
    return (
      <Card className="border-2 border-zinc-200">
        <CardContent className="py-12 text-center text-zinc-400">
          <Note size={40} className="mx-auto mb-3" />
          <p>No activity yet</p>
          <p className="text-sm mt-1">Notes and photos will appear here</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div ref={containerRef}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-zinc-500">
          {total} items from {visitsCount} visit{visitsCount !== 1 ? "s" : ""}
        </div>
        <Button variant="ghost" size="sm" onClick={refresh}>
          <ArrowsClockwise size={16} className="mr-1" />
          Refresh
        </Button>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedByVisit).map(([visitId, group]) => (
          <div key={visitId} className="relative">
            {/* Visit header */}
            {group.visit && (
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-full bg-[#FF6600]/10 flex items-center justify-center">
                  <CalendarBlank size={16} className="text-[#FF6600]" />
                </div>
                <div>
                  <span className="text-sm font-medium">
                    Visit on {format(new Date(group.visit.scheduled_start), "MMM d, yyyy")}
                  </span>
                  <Badge variant="outline" className="ml-2 text-xs capitalize">
                    {group.visit.status}
                  </Badge>
                </div>
              </div>
            )}

            {/* Items for this visit */}
            <div className="ml-4 pl-4 border-l-2 border-zinc-200 space-y-3">
              {group.items.map((item) => (
                <TimelineItem key={item.id} item={item} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Load more trigger */}
      {hasMore && (
        <div ref={loadMoreRef} className="py-4 text-center">
          {loadingMore ? (
            <div className="text-sm text-zinc-400">Loading more...</div>
          ) : (
            <div className="text-sm text-zinc-300">Scroll for more</div>
          )}
        </div>
      )}
    </div>
  );
}

// Individual timeline item component
function TimelineItem({ item }) {
  const [expanded, setExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const isNote = item.type === "note";
  const isInternal = item.visibility === "internal";

  const photoForLightbox = !isNote ? {
    photo_id: item.id,
    file_url: item.file_url,
    thumbnail_url: item.thumbnail_url,
    caption: item.caption,
    visibility: item.visibility,
  } : null;

  return (
    <div className={`p-3 rounded-sm border ${isInternal ? "bg-zinc-50 border-zinc-200" : "bg-emerald-50 border-emerald-200"}`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isNote ? "bg-blue-100 text-blue-600" : "bg-purple-100 text-purple-600"}`}>
          {isNote ? <Note size={16} /> : <ImageIcon size={16} />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium">{item.author_name}</span>
            <span className="text-xs text-zinc-400">
              {format(new Date(item.created_at), "h:mm a")}
            </span>
            <Badge
              variant={isInternal ? "secondary" : "default"}
              className={`text-xs ${isInternal ? "bg-zinc-200 text-zinc-600" : "bg-emerald-500 text-white"}`}
            >
              {isInternal ? <EyeSlash size={10} className="mr-1" /> : <Eye size={10} className="mr-1" />}
              {isInternal ? "Internal" : "Customer"}
            </Badge>
          </div>

          {isNote ? (
            <p className="text-sm mt-1 text-zinc-700 whitespace-pre-wrap">
              {item.content}
            </p>
          ) : (
            <>
              <div className="mt-2">
                <PhotoThumbnail
                  photo={photoForLightbox}
                  onClick={() => setLightboxOpen(true)}
                  showVisibilityBadge={false}
                  showActions={false}
                  className="w-32 h-32"
                />
                {item.caption && (
                  <p className="text-xs text-zinc-500 mt-1">{item.caption}</p>
                )}
              </div>
              <PhotoLightbox
                open={lightboxOpen}
                onOpenChange={setLightboxOpen}
                photo={photoForLightbox}
                canEdit={false}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
